// Client for the /travel API (trip CRUD). Mirrors ProjectsService: trips are
// read into the OS Travel zone and edited from the SERGA SHELL (writes hit the
// live API; local files reconcile on the next sync pull).

export interface TripLeg {
  mode?: string;
  from?: string;
  to?: string;
  date?: string;
  ref?: string | null;
  status?: string;
}
export interface TripStay {
  name?: string | null;
  city?: string;
  checkIn?: string;
  checkOut?: string;
  ref?: string | null;
  status?: string;
}
export interface TripCheck {
  label: string;
  done?: boolean;
}
export interface Trip {
  id: string;
  destination: string;
  title: string;
  status: string;
  dates?: { start?: string | null; end?: string | null; flexible?: boolean };
  purpose?: string;
  linkedProject?: string;
  transport?: TripLeg[];
  accommodation?: TripStay[];
  checklist?: TripCheck[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

const API_BASE_URL = "https://api.serga.ba";

export class TravelService {
  private static authTokens: any = null;

  static setAuthTokens(tokens: any) {
    this.authTokens = tokens;
  }

  private static getAuthHeaders() {
    if (!this.authTokens?.idToken) {
      throw new Error("No authentication token available");
    }
    return {
      Authorization: `Bearer ${this.authTokens.idToken}`,
      "Content-Type": "application/json",
    };
  }

  private static async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: { ...this.getAuthHeaders(), ...(options.headers || {}) },
    });
    if (!response.ok) {
      const errorBody = await response.text();
      let message = `Request failed: ${response.status}`;
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed.error) message = parsed.error;
      } catch {}
      throw new Error(message);
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  static async listTrips(): Promise<Trip[]> {
    const data = await this.request<{ trips: Trip[] }>("/travel");
    return data.trips || [];
  }

  static async getTrip(tripId: string): Promise<Trip> {
    return this.request<Trip>(`/travel/${tripId}`);
  }

  static async createTrip(input: Partial<Trip>): Promise<Trip> {
    return this.request<Trip>("/travel", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  static async updateTrip(tripId: string, input: Partial<Trip>): Promise<Trip> {
    return this.request<Trip>(`/travel/${tripId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  static async deleteTrip(tripId: string): Promise<void> {
    await this.request(`/travel/${tripId}`, { method: "DELETE" });
  }
}
